import React from 'react';
import { Link } from 'react-router-dom';
import { Shield } from 'lucide-react';
import PublicNavbar from '../components/PublicNavbar';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-purple-50 flex flex-col">
      <PublicNavbar />

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12 flex-1">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
          <p className="text-gray-500 mb-8">Last updated: December 24, 2025</p>

          <div className="prose prose-gray max-w-none">
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">1. Information We Collect</h2>
            <p className="text-gray-600 mb-4">
              SmartDaddy AI collects information you provide directly to us, including:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
              <li>Account information (name, email, password)</li>
              <li>Family member profiles (names, dates of birth)</li>
              <li>Activity and event information</li>
              <li>Calendar data from connected sources</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">2. How We Use Your Information</h2>
            <p className="text-gray-600 mb-4">
              We use the information we collect to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
              <li>Provide, maintain, and improve our services</li>
              <li>Generate AI-powered summaries and insights</li>
              <li>Send notifications about your family's activities</li>
              <li>Respond to your comments and questions</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">3. Data Security & Compliance</h2>
            <p className="text-gray-600 mb-4">
              We take the security of your data extremely seriously. SmartDaddy AI implements industry-leading 
              cybersecurity best practices:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-4">
              <li><strong>100% US-Based:</strong> All data and information is stored and processed entirely within the United States.</li>
              <li><strong>NIST 800-53:</strong> We follow the National Institute of Standards and Technology (NIST) Special Publication 800-53 security and privacy controls framework.</li>
              <li><strong>ISO 27001:</strong> Our security practices align with ISO 27001 international standards for information security management systems (ISMS).</li>
              <li>Your data is encrypted in transit (TLS 1.3) and at rest (AES-256).</li>
              <li>We never sell your personal information to third parties.</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">4. Payment Security</h2>
            <p className="text-gray-600 mb-4">
              All payments are processed securely through <strong>Stripe</strong>, a PCI-DSS Level 1 certified payment processor—the highest level of certification in the payment industry.
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
              <li><strong>PCI-DSS Compliant:</strong> All payment processing meets Payment Card Industry Data Security Standard (PCI-DSS) requirements.</li>
              <li><strong>Zero Payment Data Storage:</strong> SmartDaddy AI never stores, processes, or has access to your credit card numbers, CVV codes, or other sensitive payment information.</li>
              <li>Payment information is transmitted directly to Stripe's secure servers and never touches our systems.</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">5. Data Retention</h2>
            <p className="text-gray-600 mb-6">
              We retain your information for as long as your account is active or as needed to 
              provide you services. You can request deletion of your data at any time by contacting us.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">6. Children's Privacy</h2>
            <p className="text-gray-600 mb-6">
              SmartDaddy AI is designed to help parents manage their children's activities. 
              We do not knowingly collect personal information directly from children under 13. 
              Parents maintain full control over their children's information.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-4">7. Contact Us</h2>
            <p className="text-gray-600 mb-4">
              If you have questions about this Privacy Policy, please{' '}
              <Link to="/contact" className="text-blue-500 hover:underline">contact us</Link>.
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-100 py-8">
        <div className="max-w-6xl mx-auto px-4 flex flex-wrap justify-between items-center">
          <p className="text-xs text-gray-400">
            © 2025 SmartDaddy AI. All rights reserved. | An{' '}
            <a href="https://offworldventures.org" target="_blank" rel="noopener noreferrer" className="hover:text-gray-600">
              OffWorld Ventures
            </a>{' '}
            company.
          </p>
          <div className="flex gap-6 text-xs text-gray-400">
            <Link to="/privacy" className="hover:text-gray-600 font-medium text-gray-600">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-gray-600">Terms of Use</Link>
            <Link to="/contact" className="hover:text-gray-600">Contact Us</Link>
            <Link to="/pricing" className="hover:text-gray-600">Pricing</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPage;
